import React, {useState, useEffect} from 'react';
import {Button, Grid, Modal} from 'semantic-ui-react';
import {useSiteHealth} from '../../providers/SiteHealthProvider';

const EquipmentErrorModal = ({
  equipmentId,
  statusDateTime,
  backgroundColor,
  clickable,
  points,
  functions,
}) => {
  const {equipmentData, clearEquipmentData, getEquipmentData} = useSiteHealth();
  return (
    <Modal
      trigger={
        <Button disabled={!clickable} style={{backgroundColor: 'transparent'}}>
          <div
            style={{
              width: '30px',
              height: '30px',
              border: '1px solid black',
              borderRadius: '15px',
              backgroundColor,
              cursor: clickable ? 'pointer' : 'default',
            }}
          ></div>
        </Button>
      }
      header={(points ? 'Point' : 'Function') + ' Details'}
      actions={['Close']}
      size={'large'}
      onOpen={() => {
        clearEquipmentData();
        getEquipmentData(equipmentId, statusDateTime.toISOString());
      }}
      content={
        <div>
          {equipmentData != null && (
            <Grid columns={5} celled={'internally'} padded>
              <Grid.Row>
                <Grid.Column>
                  <span style={{fontSize: 16, fontWeight: 'bold'}}>Name</span>
                </Grid.Column>
                <Grid.Column>
                  <span style={{fontSize: 16, fontWeight: 'bold'}}>Value</span>
                </Grid.Column>
                <Grid.Column>
                  <span style={{fontSize: 16, fontWeight: 'bold'}}>Status</span>
                </Grid.Column>
                <Grid.Column>
                  <span style={{fontSize: 16, fontWeight: 'bold'}}>Message</span>
                </Grid.Column>
                <Grid.Column>
                  <span style={{fontSize: 16, fontWeight: 'bold'}}>Timestamp</span>
                </Grid.Column>
              </Grid.Row>
              {points &&
                equipmentData.points &&
                equipmentData.points.map(p => (
                  <Grid.Row key={p.objectPointId + 'row'}>
                    <Grid.Column key={p.objectPointId + 'name'}>
                      <span>{p.pointCode}</span>
                    </Grid.Column>
                    <Grid.Column key={p.objectPointId + 'value'}>
                      <span>{p.value}</span>
                    </Grid.Column>
                    <Grid.Column key={p.objectPointId + 'error'}>
                      <span style={{color: p.error ? 'red' : 'green'}}>
                        {p.error ? 'invalid' : 'valid'}
                      </span>
                    </Grid.Column>
                    <Grid.Column key={p.objectPointId + 'message'}>
                      <span>{p.errorMessage}</span>
                    </Grid.Column>
                    <Grid.Column key={p.objectPointId + 'time'}>
                      <span>{new Date(equipmentData.normalizedTime).toLocaleString()}</span>
                    </Grid.Column>
                  </Grid.Row>
                ))}
              {functions &&
                equipmentData.functions &&
                equipmentData.functions.map(f => (
                  <Grid.Row key={f.id + 'row'}>
                    <Grid.Column key={f.id + 'name'}>
                      <span>{f.name}</span>
                    </Grid.Column>
                    <Grid.Column key={f.id + 'value'}>
                      <span>{f.value}</span>
                    </Grid.Column>
                    <Grid.Column key={f.id + 'error'}>
                      <span style={{color: f.error ? 'red' : 'green'}}>
                        {f.error ? 'invalid' : 'valid'}
                      </span>
                    </Grid.Column>
                    <Grid.Column key={f.id + 'message'}>
                      <span>{f.errorMessage}</span>
                    </Grid.Column>
                    <Grid.Column key={f.id + 'time'}>
                      <span>{new Date(equipmentData.normalizedTime).toLocaleString()}</span>
                    </Grid.Column>
                  </Grid.Row>
                ))}
            </Grid>
          )}
        </div>
      }
    />
  );
};

const EquipmentHealthIndicator = ({
  label,
  value,
  equipmentId,
  statusDateTime,
  points,
  functions,
}) => {
  let backgroundColor = 'green';

  if (value === 1) {
    backgroundColor = 'yellow';
  } else if (value === 2) {
    backgroundColor = 'red';
  } else if (value === -1) {
      backgroundColor = 'gray';
  }

  return (
    <div
      style={{
        fontSize: '14px',
        color: 'black',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <div>{label}</div>
      <EquipmentErrorModal
        equipmentId={equipmentId}
        statusDateTime={statusDateTime}
        backgroundColor={backgroundColor}
        clickable={points || functions}
        points={points}
        functions={functions}
      />
    </div>
  );
};

export const EquipmentHealthComponent = ({data, statusDateTime, nestedLevel = 0, equipmentRef, showAllEquipment}) => {
  const [showChildren, setShowChildren] = useState(false);
  const [scrollTo, setScrollTo] = useState(null);
  const backgroundColor = `rgba(0,0,0,${nestedLevel * 0.05}`;

  var isParent = data.children.length > 0 ? true : false;
  var error = false;
  if (data.value.pointHealth + data.value.childrenHealth + data.value.functionHealth > 0) {
    error = true;
  }

  useEffect(() => {
    if (scrollTo != null && showChildren) {
      equipmentRef.current[scrollTo].current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      setScrollTo(null);
    }
  }, [showChildren, scrollTo, equipmentRef]);

  if ((!showAllEquipment && data.value.pointHealth === -1 && data.value.functionHealth  === -1 && data.value.childrenHealth === -1)) {
      return null;
  }

  return (
    <div key={data.value.id}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          border: '1px solid black',
          minHeight: '100px',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '10px',
          fontSize: '24px',
          color: '#153A4B',
          backgroundColor,
        }}
      >
        <div
          onClick={() => setShowChildren(!showChildren)}
          style={{
            width: '100px',
            paddingLeft: '10px',
            cursor: data.children.length ? 'pointer' : 'default',
          }}
        >
          {data.children.length ? (showChildren ? '▿' : '▹') : '□︎'}
        </div>
        <div style={{flexGrow: 3}}>{data.value.name}</div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-evenly',
            flexGrow: 3,
            maxWidth: '225px',
          }}
        >
          <EquipmentHealthIndicator
            label={'Points'}
            value={data.value.pointHealth}
            equipmentId={data.value.id}
            statusDateTime={statusDateTime}
            points
          />
          <EquipmentHealthIndicator
            label={'Functions'}
            value={data.value.functionHealth}
            equipmentId={data.value.id}
            statusDateTime={statusDateTime}
            functions
          />
          <span
            style={{cursor: (error && isParent && !showChildren) !== false ? 'pointer' : 'default'}}
            onClick={() => {
              if (error && isParent && showChildren === false) {
                let child = data.children.find(
                  e => e.value.childrenHealth + e.value.pointHealth + e.value.functionHealth > 0
                );

                setShowChildren(!showChildren);
                setScrollTo(child.value.id);
              }
            }}
          >
            <EquipmentHealthIndicator
              label={'Children'}
              value={data.value.childrenHealth}
              equipmentId={data.value.id}
              statusDateTime={statusDateTime}
            />
          </span>
        </div>
      </div>
      <div
        style={{
          paddingLeft: '10px',
        }}
      >
        {showChildren
          ? data.children.map(child => (
              <div key={child.value.id} ref={equipmentRef.current[child.value.id]}>
                <EquipmentHealthComponent
                  data={child}
                  statusDateTime={statusDateTime}
                  nestedLevel={nestedLevel + 1}
                  key={child.value.id}
                  equipmentRef={equipmentRef}
                  showAllEquipment={showAllEquipment}
                />
              </div>
            ))
          : null}
      </div>
    </div>
  );
};
